import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, CircleDot } from "lucide-react";

export default function WebcamCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (e) {
        toast.error("Camera access denied. Allow webcam or use file upload.");
        onClose?.();
      }
    }
    start();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const snap = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // mirror horizontally so the user sees a natural selfie
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const b64 = dataUrl.split(",")[1];
    onCapture?.({ dataUrl, base64: b64 });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="webcam-modal">
      <div className="px-6 py-4 border-b border-[var(--border-bright)] flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Camera className="w-5 h-5 text-[var(--primary)]" />
          <span className="tape-label">LIVE CAPTURE</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            {ready ? "● STREAM ACTIVE" : "INITIALIZING..."}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-[var(--text-muted)] hover:text-[var(--primary)]"
          data-testid="webcam-close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative">
        <div className="relative max-w-2xl w-full aspect-square panel-bright overflow-hidden">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
            playsInline
            muted
            data-testid="webcam-video"
          />
          {/* HUD */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 brackets">
              <span className="b-tr" />
              <span className="b-bl" />
            </div>
            <div className="absolute top-3 left-3 right-3 flex justify-between text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
              <span className="bg-black/70 px-2 py-1 flicker">● LIVE FEED</span>
              <span className="bg-black/70 px-2 py-1">{ready ? "READY" : "BUFFERING…"}</span>
            </div>
            <div className="absolute bottom-3 left-3 right-3 text-center text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
              CENTER FACE INSIDE BRACKETS
            </div>
          </div>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="px-6 py-5 border-t border-[var(--border-bright)] flex items-center justify-center gap-4">
        <button
          onClick={snap}
          disabled={!ready}
          className="btn-brutal flex items-center gap-2 text-lg"
          data-testid="webcam-snap"
        >
          <CircleDot className="w-5 h-5" /> CAPTURE
        </button>
        <button onClick={onClose} className="btn-ghost" data-testid="webcam-cancel">
          Cancel
        </button>
      </div>
    </div>
  );
}
